/**
 * OMNative Desktop App Interface
 *
 * 통신 패턴:
 * 1. Web → Desktop: refreshToken()
 * 2. Desktop → Web: onDate(), onTime(), onLanguage()
 * 3. Desktop → Web (직접 호출): window.changeLanguage(), window.onWebChatActivated()
 */

import { store } from 'index'

interface OMNativeInterface {
  refreshToken: (callback: (token: string) => void) => void
}

declare global {
  interface Window {
    OMNative?: OMNativeInterface
    onWebChatActivated?: () => void
    changeLanguage?: (language: string) => void
    onDate?: (dateFormat: string) => void
    onTime?: (timeFormat: string) => void
    onLanguage?: (language: string) => void
    onNavigateToChat?: (roomType: string, roomId: string, messageId?: string) => void
  }
}

class NativeInterface {
  /**
   * Native 사용 가능 여부 확인
   */
  static isAvailable(): boolean {
    const isAvailable = typeof window !== 'undefined' && window.OMNative != null
    if (isAvailable) {
      console.log(`[NativeInterface] isAvailable:`, isAvailable)
    }
    return isAvailable
  }

  /**
   * Native → Web 콜백 등록
   * 네이티브 앱에서 window.* 함수를 호출할 수 있도록 등록
   */
  static register(): void {
    window.onWebChatActivated = () => {
      console.log('[NativeInterface] onWebChatActivated')
    }

    /**
     * Desktop에서 언어 변경 명령
     * @param {string} language - locale code (예: 'ko-KR', 'en-US')
     */
    window.changeLanguage = (language: string) => {
      console.log('[NativeInterface] changeLanguage:', language)

      if (!language) {
        console.warn('[NativeInterface] changeLanguage: invalid language', language)
        return
      }

      store.dispatch({
        type: 'settings/SET_LOCALE',
        payload: { locale: language },
      })
    }

    /**
     * Desktop의 날짜 포맷 설정 변경 이벤트
     * @param {string} dateFormat - 날짜 포맷 (예: 'YYYY-MM-DD', 'MM/DD/YYYY')
     */
    window.onDate = (dateFormat: string) => {
      console.log('[NativeInterface] onDate:', dateFormat)

      if (!dateFormat) {
        console.warn('[NativeInterface] onDate: invalid dateFormat', dateFormat)
        return
      }

      store.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: { 'general.dateFormat': dateFormat },
      })
    }

    /**
     * Desktop의 시간 포맷 설정 변경 이벤트
     * @param {string} timeFormat - 시간 포맷 (예: 'HH:mm', 'hh:mm A')
     */
    window.onTime = (timeFormat: string) => {
      console.log('[NativeInterface] onTime:', timeFormat)

      if (!timeFormat) {
        console.warn('[NativeInterface] onTime: invalid timeFormat', timeFormat)
        return
      }

      store.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: { 'general.timeFormat': timeFormat },
      })
    }

    /**
     * Desktop의 언어 설정 변경 이벤트
     * @param {string} language - locale code (예: 'ko-KR', 'en-US')
     */
    window.onLanguage = (language: string) => {
      console.log('[NativeInterface] onLanguage:', language)

      if (!language) {
        console.warn('[NativeInterface] onLanguage: invalid language', language)
        return
      }

      store.dispatch({
        type: 'settings/SET_LOCALE',
        payload: { locale: language },
      })
    }

    /**
     * Desktop에서 채팅으로 이동 이벤트
     * @param {string} roomType - 채팅방 타입 ('channel' | 'direct_message')
     * @param {string} roomId - 채팅방 ID
     * @param {string} [messageId] - 메시지 ID (선택)
     */
    window.onNavigateToChat = (roomType: string, roomId: string, messageId?: string) => {
      console.log('[NativeInterface] onNavigateToChat:', { roomType, roomId, messageId })

      if (!roomId) {
        console.warn('[NativeInterface] onNavigateToChat: invalid roomId', roomId)
        return
      }

      // roomType 변환: channel → ch, direct_message → dm
      let pathType: string
      if (roomType === 'channel') {
        pathType = 'ch'
      } else if (roomType === 'direct_message') {
        pathType = 'dm'
      } else {
        console.warn('[NativeInterface] onNavigateToChat: unknown roomType', roomType)
        return
      }

      // path 생성: /chat/{type}/{roomId} 또는 /chat/{type}/{roomId}/{messageId}
      let path = `/chat/${pathType}/${roomId}`
      if (messageId != null && messageId !== '') {
        path += `/${messageId}`
      }

      // Desktop session은 항상 /chat 경로이므로 이벤트로 UI 업데이트
      console.log('[NativeInterface] onNavigateToChat: navigating to', path)
      window.dispatchEvent(
        new CustomEvent('push-notification-navigate', {
          detail: { path },
        }),
      )
    }
  }

  /**
   * 토큰 갱신 요청 (Web → Desktop)
   * @returns {Promise<string>} Access token
   */
  static async refreshToken(): Promise<string> {
    if (!this.isAvailable()) {
      console.info('[NativeInterface] OMNative is not available')
      return ''
    }

    return new Promise((resolve) => {
      try {
        window.OMNative!.refreshToken((token: string) => {
          if (token) {
            resolve(token)
          } else {
            console.info('[NativeInterface] OMNative returned empty token', token)
            resolve('')
          }
        })
      } catch (error) {
        console.info('[NativeInterface] Failed to refresh token', error)
        resolve('')
      }
    })
  }
}

export default NativeInterface
